// The face-placing tools shared by the face placer (Placer.tsx): moving, sizing and tilting a face or one of its
// parts, and the compact controls under the placement picture.
import type { FaceTurn } from "../../model/faceStyles";
import { useState, type ReactNode } from "react";
import { turn } from "../../engine/motion";
import type { FaceSpots, Spot, Viseme } from "../../model/face";
import { Icon } from "../common";

type Part = "face" | "eyes" | "brows" | "mouth";
export type { Part };
export const PARTS: [Part, string][] = [["face", "Whole face"], ["eyes", "Eyes"], ["brows", "Brows"], ["mouth", "Mouth"]];
export const TALK: Viseme[] = ["B", "C", "D", "E", "F", "C", "B", "A", "G", "H", "X"];

export const scaleFace = <F extends FaceSpots>(f: F, k: number): F => {
  const around = (s: Spot): Spot => ({ x: f.eyes.x + (s.x - f.eyes.x) * k, y: f.eyes.y + (s.y - f.eyes.y) * k, w: s.w * k });
  return { ...f, eyes: { ...f.eyes, w: f.eyes.w * k }, brows: around(f.brows), mouth: around(f.mouth) };
};
export const shift = (s: Spot, dx: number, dy: number): Spot => ({ ...s, x: s.x + dx, y: s.y + dy });
// The face turned to `deg` degrees clockwise, around the middle between its eyes and mouth.
export const tiltFace = <F extends FaceSpots>(f: F, deg: number): F => {
  const by = deg - (f.tilt ?? 0), cx = (f.eyes.x + f.mouth.x) / 2, cy = (f.eyes.y + f.mouth.y) / 2;
  const around = (s: Spot): Spot => { const d = turn(s.x - cx, s.y - cy, by); return { ...s, x: cx + d.x, y: cy + d.y }; };
  const { tilt, ...rest } = f;
  return { ...rest, eyes: around(f.eyes), brows: around(f.brows), mouth: around(f.mouth), ...(deg ? { tilt: deg } : {}) } as F;
};

// The compact controls under a placement picture: which part to move, one slider for size or tilt, and test-the-mouth button. `children` goes above them.
const TURNS: [FaceTurn | undefined, string][] = [["left", "Looks left"], [undefined, "Front"], ["right", "Looks right"]];

// `onTurn`: also offer which way the face looks (straight on, or 45° left or right).
export function FaceTools({ face, part, onPart, maxW, onSize, onTilt, onTurn, talk, onTalk, children }: {
  face: FaceSpots; part: Part; onPart: (p: Part) => void; maxW: number; onSize: (w: number) => void; onTilt: (deg: number) => void;
  onTurn?: (turn: FaceTurn | undefined) => void; talk: boolean; onTalk: (t: boolean) => void; children?: ReactNode;
}) {
  const [mode, setMode] = useState<"size" | "tilt">("size");
  const tilt = part === "face" && mode === "tilt";
  const [min, max, value] = tilt ? [-60, 60, Math.round(face.tilt ?? 0)] : [maxW * 0.02, maxW, part === "face" ? face.eyes.w : face[part].w];
  const p = ((value - min) / (max - min)) * 100;
  return (
    <div className="face-tools">
      {children}
      {onTurn && (
        <div className="mini-seg" role="radiogroup" aria-label="Which way the face looks">
          {TURNS.map(([k, l]) => <button key={l} role="radio" aria-checked={face.turn === k} className={face.turn === k ? "on" : ""} onClick={() => onTurn(k)}>{l}</button>)}
        </div>
      )}
      <div className="mini-seg" role="radiogroup" aria-label="What to move">
        {PARTS.map(([k, l]) => <button key={k} role="radio" aria-checked={part === k} className={part === k ? "on" : ""} onClick={() => onPart(k)}>{l}</button>)}
      </div>
      <div className="mini-row">
        {part === "face" && (
          <div className="mini-seg two" role="radiogroup" aria-label="Slider">
            {(["size", "tilt"] as const).map(m => <button key={m} role="radio" aria-checked={mode === m} className={mode === m ? "on" : ""} onClick={() => setMode(m)}>{m === "size" ? "Size" : "Tilt"}</button>)}
          </div>
        )}
        <label className="slider mini-slider">
          <input type="range" min={min} max={max} step={1} value={value} aria-label={tilt ? "Tilt" : "Size"} style={{ ["--p" as string]: `${p}%` }}
            onChange={e => (tilt ? onTilt(Number(e.target.value)) : onSize(Number(e.target.value)))} />
        </label>
        <button className={`icon-btn mini${talk ? " on" : ""}`} onClick={() => onTalk(!talk)} aria-label={talk ? "Stop talking" : "Test the mouth"} title={talk ? "Stop talking" : "Test the mouth"}><Icon name="voice" size={20} /></button>
      </div>
    </div>
  );
}
